import { useEffect, useMemo, useState } from "react";
import {
  getDetailCurrentReport,
  getLatestFormsByBusinessUnit,
} from "../../../apis";
import { getListBusiness } from "../../../apis/apiClientConfig";
import { Loading } from "../../Loading/String/Loading";
import { PreviewForm } from "../PreviewForm/PreviewForm";
import { formStarter } from "../PreviewForm/formStarter";
import { EGBasicSelect } from "../../MainComponents/Select/EGBasicSelect";
import { Paper, Typography } from "@mui/material";
import { Building } from "../../Icon";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// Valor centinela para la fila del formulario default del tenant (business_unit
// null). No es "" para poder distinguir "el usuario aun no elige" de "el usuario
// eligio el default".
const DEFAULT_FORM_KEY = "__default__";

export const NewReport = () => {
  const [t] = useTranslation("forms");
  const { gTheme } = useSelector((state) => state.theme);

  // Unidades disponibles (ultimo formulario por unidad). Cada item trae al menos
  // { id, business_unit }.
  const [buForms, setBuForms] = useState(null);
  // Mapa alias -> nombre legible de la unidad de negocio. El endpoint de
  // formularios solo devuelve la llave (business_unit); el nombre que ve el
  // usuario vive en la config del cliente (getListBusiness). null = aun no
  // carga; {} = cargo pero sin unidades / fallo (se cae a mostrar la llave).
  const [buNameByAlias, setBuNameByAlias] = useState(null);
  // La unidad elegida en el selector. null = aun no elige (eleccion forzada).
  const [selectedKey, setSelectedKey] = useState(null);

  const [formLoaded, setFormLoaded] = useState(null);
  const [steps, setSteps] = useState(null);
  const [schemaState, setSchemaState] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isValid, setIsValid] = useState(null);
  const [constraints, setConstraints] = useState([]);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  // Opciones del selector, a partir de la lista de formularios por unidad. El
  // label se resuelve al nombre legible de la unidad (buNameByAlias); si no se
  // encuentra —config aun cargando, unidad sin nombre, o fallo del endpoint—
  // se cae a la llave cruda para no dejar la opcion en blanco.
  //
  // Regla: si el tenant tiene al menos un formulario configurado por unidad, se
  // ofrecen SOLO esas unidades y el formulario default (business_unit null) NO
  // se ofrece — con una sola unidad configurada no vale la pena mostrar "unidad
  // vs predeterminado", se va directo a la configurada. El default solo entra en
  // juego cuando el tenant no tiene ninguna unidad configurada (cliente legacy),
  // y ahi es la unica opcion.
  //
  // Punto de extension para permisos/segmentacion: hoy se muestran todas las
  // unidades configuradas. Cuando User_Schemas.business_unit este poblado y
  // exista la regla de negocio, aqui se filtraria por las unidades que el
  // usuario puede capturar (misma semantica de business_unit_matches / bu_path
  // en el backend).
  const options = useMemo(() => {
    if (!Array.isArray(buForms)) return [];

    const nameFor = (alias) => buNameByAlias?.[alias] || alias;

    const configured = buForms.filter((form) => form.business_unit);

    if (configured.length > 0) {
      return configured.map((form) => ({
        key: form.business_unit,
        label: nameFor(form.business_unit),
      }));
    }

    // Sin unidades configuradas: el default del tenant es la unica opcion.
    const hasDefault = buForms.some((form) => !form.business_unit);
    return hasDefault
      ? [{ key: DEFAULT_FORM_KEY, label: t("NewReport.default_form") }]
      : [];
  }, [buForms, buNameByAlias, t]);

  // Una sola opcion: no tiene sentido forzar a elegir entre una. Se
  // autoselecciona y se comporta como el casemanager actual. Cubre tanto al
  // tenant con exactamente una unidad configurada (va directo a ella) como al
  // legacy sin unidades (va directo al default).
  const isSingleOption = options.length === 1;

  useEffect(() => {
    // Se cargan en paralelo los formularios por unidad y los nombres legibles
    // de las unidades. Un fallo en cualquiera de las dos no debe dejar la UI
    // colgada en el spinner: buForms cae a [] (estado "sin formularios") y el
    // mapa de nombres cae a {} (se muestra la llave cruda como respaldo).
    getLatestFormsByBusinessUnit()
      .then((forms) => setBuForms(Array.isArray(forms) ? forms : []))
      .catch((error) => {
        console.error(error);
        setBuForms([]);
      });

    getListBusiness()
      .then((resp) => {
        const list = resp?.data;
        const map = Array.isArray(list)
          ? list.reduce((acc, bu) => {
              if (bu?.alias) acc[bu.alias] = bu.label || bu.alias;
              return acc;
            }, {})
          : {};
        setBuNameByAlias(map);
      })
      .catch((error) => {
        console.error(error);
        setBuNameByAlias({});
      });
  }, []);

  // Autoseleccion solo cuando hay exactamente una opcion.
  useEffect(() => {
    if (isSingleOption && selectedKey === null) {
      setSelectedKey(options[0].key);
    }
  }, [isSingleOption, options, selectedKey]);

  // Al elegir una unidad, se pide su formulario vigente y se arma. El centinela
  // del default se traduce a "sin llave", que en el backend cae al form null.
  useEffect(() => {
    if (selectedKey === null) return;
    const businessUnitKey = selectedKey === DEFAULT_FORM_KEY ? "" : selectedKey;
    setIsLoadingForm(true);
    setFormLoaded(null);
    getDetailCurrentReport(businessUnitKey)
      .then((resp) => setFormLoaded(resp))
      .catch((error) => {
        console.error(error);
        setIsLoadingForm(false);
      });
  }, [selectedKey]);

  useEffect(() => {
    if (!formLoaded) return;
    formStarter(formLoaded, "normal")
      .then(
        ({
          initSteps,
          initSchemaState,
          initFormData,
          initIsValid,
          initConstraints,
        }) => {
          setSteps(initSteps);
          setSchemaState(initSchemaState);
          setFormData(initFormData);
          setIsValid(initIsValid);
          setConstraints(initConstraints);
          setIsLoadingForm(false);
        }
      )
      .catch((error) => {
        console.log(error);
        setIsLoadingForm(false);
      });
  }, [formLoaded]);

  const handleSelect = (event) => {
    const value = event.target.value;
    setSelectedKey(value === "" ? null : value);
  };

  const hasChosen = selectedKey !== null;

  return (
    <div
      id="settings"
      className={`report-grid ${gTheme !== "light" ? "dark-mode" : ""}`}
    >
      {/* Selector de unidad de negocio. Con una sola opcion no se muestra (se
          autoselecciona), replicando el comportamiento previo. Va en su propia
          tarjeta (Paper con borde) para quedar visualmente consistente con la
          tarjeta del formulario de abajo, en vez de flotar suelto. */}
      {!isSingleOption && Array.isArray(buForms) && options.length > 0 && (
        <Paper
          variant="outlined"
          className="new-report-bu-selector"
          sx={{
            maxWidth: 420,
            mx: "auto",
            mb: 2,
            p: 2,
            borderRadius: 2,
          }}
        >
          <EGBasicSelect
            label={t("NewReport.select_business_unit")}
            placeholder={t("NewReport.select_business_unit_placeholder")}
            value={selectedKey ?? ""}
            onChange={handleSelect}
            options={options.map((opt) => ({
              value: opt.key,
              label: opt.label,
            }))}
            required
            // Neutraliza el borde/label rojo una vez que hay unidad elegida:
            // con isValid=true (y showSuccessValidation por defecto en false)
            // EGBasicSelect usa el color neutro, no el verde de exito. Sin
            // eleccion se deja en false para conservar el rojo de "requerido".
            isValid={hasChosen}
            showHelpText={false}
            name="new-report-bu"
          />
        </Paper>
      )}

      {buForms === null ? (
        <Loading />
      ) : options.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            maxWidth: 420,
            mx: "auto",
            mt: 4,
            p: 4,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
          }}
        >
          <Building size={44} color="#94a3b8" />
          <Typography color="text.secondary">
            {t("NewReport.no_forms_available")}
          </Typography>
        </Paper>
      ) : !hasChosen ? (
        <Paper
          variant="outlined"
          sx={{
            maxWidth: 420,
            mx: "auto",
            mt: 4,
            p: 4,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
            borderStyle: "dashed",
          }}
        >
          <Building size={44} color="#94a3b8" />
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {t("NewReport.please_select_business_unit_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("NewReport.please_select_business_unit")}
          </Typography>
        </Paper>
      ) : isLoadingForm || !steps ? (
        <Loading />
      ) : (
        <PreviewForm
          steps={steps}
          schemaState={schemaState}
          setSchemaState={setSchemaState}
          formData={formData}
          setFormData={setFormData}
          isValid={isValid}
          setIsValid={setIsValid}
          constraints={constraints}
          setConstraints={setConstraints}
          showButtons={true}
          stepClick={false}
        />
      )}
    </div>
  );
};
